/**
 * LineSplitModal.jsx — cut one line into two, at a sentence boundary.
 *
 * ⭐ WHY A SENTENCE BOUNDARY AND NOT A FREE CURSOR. The server enforces that the parts add up to the
 * original text, whitespace aside. Choosing a boundary from a list the component derived by SPLITTING the
 * original string makes that invariant true by construction — there is no code path here that can drop or
 * invent a word, so the control needs no confirmation step and the operator needs no proofreading pass.
 * A free-text pair of boxes would put a typo between the author and the audiobook.
 *
 * ⭐ THE CASE IT WAS BUILT FOR. Mask ch33: *"…Wanna know the real story? My theory, at least? Forrbank
 * realized he'd been holding his breath."* — three sentences, of which the last is the Narrator's and the
 * ingest glued it to Diogenes' dialogue. Quote marks are stripped during ingest (45 of Mask's 1,199 lines
 * retain any), so the splitter could not see where the quotation closed.
 *
 * ⚠️ NOTHING IS AUDIBLE UNTIL THE PARTS ARE RENDERED and the chapter re-stitched. The modal says so on the
 * way out rather than reporting a bare success, because "saved" reading as "done" is the shape of most of
 * this project's expensive afternoons.
 */

import React from "react";
import { Modal, Button, Icon, Message, Dropdown, Label } from "semantic-ui-react";

/**
 * Break text into sentences, keeping every character.
 *
 * ⛔ The pieces MUST re-join to the original — that is the whole safety argument — so this splits on a
 * boundary and keeps the delimiter with the sentence it ends, rather than tokenising and rebuilding.
 * ⚠️ Deliberately crude: it does not know that "Mr." is not a sentence end. A wrong boundary offers the
 * operator a cut they will not choose; a LOST character would be a defect, and cannot happen here.
 */
export const sentencesOf = (text) => {
  const src = String(text == null ? "" : text);
  if (!src.trim()) return [];
  const out = [];
  const re = /[^.!?…]*[.!?…]+["'’”]*\s*|[^.!?…]+$/gu;
  let m;
  while ((m = re.exec(src)) !== null) {
    if (m[0]) out.push(m[0]);
    if (re.lastIndex === m.index) re.lastIndex += 1; // never loop on a zero-width match
  }
  // Fallback: one sentence. The join check below is what actually guarantees correctness.
  return out.length ? out : [src];
};

const TYPE_OPTIONS = [
  { key: "narration", value: "narration", text: "narration", description: "read by the Narrator" },
  { key: "dialogue", value: "dialogue", text: "dialogue", description: "spoken by a character" },
  { key: "note", value: "note", text: "note", description: "a footnote or sidenote" },
  { key: "inlineQuote", value: "inlineQuote", text: "inlineQuote", description: "a quoted fragment" },
];

export const LineSplitModal = ({ open, row, onClose, onSplit, busy, error }) => {
  const sentences = React.useMemo(() => sentencesOf(row?.text), [row?.text]);
  // The boundary is "the first sentence that belongs to the SECOND part". Starts at the last sentence,
  // because a glued narration clause is almost always the tail — the case this exists for.
  const [cut, setCut] = React.useState(Math.max(1, sentences.length - 1));
  const [tailType, setTailType] = React.useState("narration");
  const [tailSpeaker, setTailSpeaker] = React.useState("Narrator");

  React.useEffect(() => {
    setCut(Math.max(1, sentences.length - 1));
    setTailType("narration");
    setTailSpeaker("Narrator");
  }, [sentences.length, row?.lineId]);

  if (!row) return null;

  const head = sentences.slice(0, cut).join("").trim();
  const tail = sentences.slice(cut).join("").trim();
  /**
   * ⭐ The invariant, checked HERE as well as on the server. Not because the server cannot be trusted, but
   * because a disabled button explains itself and a 400 does not. Same normalisation the server uses.
   */
  const norm = (s) => String(s || "").replace(/\s+/g, " ").trim();
  const joinsBack = norm(`${head} ${tail}`) === norm(row.text);
  const canSplit = sentences.length > 1 && head && tail && joinsBack && !busy;

  return (
    <Modal open={!!open} onClose={onClose} size="small" closeIcon>
      <Modal.Header>
        Split this line
        <Label size="tiny" style={{ marginLeft: ".6em" }}>ch{row.chapter ?? row.chapterNumber}</Label>
      </Modal.Header>
      <Modal.Content>
        <p style={{ color: "#666", fontSize: ".92em" }}>
          Choose where the line breaks. The book keeps its words — the original line stays as it is, and
          these two are rendered in its place.
        </p>

        {sentences.length < 2 ? (
          <Message warning size="small">
            <Icon name="warning sign" />
            This line has only one sentence, so there is no boundary to cut at. A mid-sentence split would
            have to be typed, and typing is how a word goes missing.
          </Message>
        ) : (
          <div className="rud-split-sentences">
            {sentences.map((s, i) => (
              <React.Fragment key={i}>
                {i > 0 && (
                  <button
                    type="button"
                    className={`rud-split-boundary${cut === i ? " rud-split-boundary-active" : ""}`}
                    onClick={() => setCut(i)}
                    aria-label={`Cut before sentence ${i + 1}`}
                  >
                    {cut === i ? <><Icon name="cut" /> cut here</> : "cut here"}
                  </button>
                )}
                <div className={`rud-split-sentence${i >= cut ? " rud-split-sentence-tail" : ""}`}>
                  {s.trim()}
                </div>
              </React.Fragment>
            ))}
          </div>
        )}

        {sentences.length > 1 && (
          <div className="rud-split-parts">
            <div>
              <strong>First part</strong>{" "}
              <span style={{ color: "#666" }}>
                keeps <em>{row.speaker || "(no speaker)"}</em> and <em>{row.type}</em> — unchanged
              </span>
            </div>
            <div style={{ marginTop: ".6em" }}>
              <strong>Second part</strong>
              <div style={{ display: "flex", gap: ".5em", alignItems: "center", marginTop: ".3em" }}>
                <Dropdown
                  selection compact options={TYPE_OPTIONS} value={tailType}
                  onChange={(_, d) => {
                    setTailType(d.value);
                    // A narration part is read by the Narrator; anything else keeps the anchor's speaker
                    // until the operator says otherwise. Guessing a character name would be worse.
                    if (d.value === "narration") setTailSpeaker("Narrator");
                    else if (tailSpeaker === "Narrator") setTailSpeaker(row.speaker || "");
                  }}
                />
                <input
                  className="rud-split-speaker"
                  value={tailSpeaker}
                  onChange={(e) => setTailSpeaker(e.target.value)}
                  placeholder="speaker"
                  aria-label="Speaker of the second part"
                />
              </div>
              {/* ⚠️ A name no character in the book answers to resolves to nothing, and a dialogue line that
                  resolves to nothing renders SILENT while the job reports success. Preflight catches it —
                  said here so it is not a surprise at the gate. */}
              <div style={{ color: "#888", fontSize: ".85em", marginTop: ".35em" }}>
                Must match a character in this book, or its alias. Preflight will refuse an unknown speaker
                rather than render it silently.
              </div>
            </div>
          </div>
        )}

        {!joinsBack && sentences.length > 1 && (
          <Message negative size="small">
            <Icon name="ban" /> The two parts no longer add up to the original line. This should be
            impossible — do not save; please report it.
          </Message>
        )}
        {error && <Message negative size="small"><Icon name="warning sign" /> {String(error)}</Message>}
      </Modal.Content>
      <Modal.Actions>
        <Button basic onClick={onClose} disabled={!!busy}>Cancel</Button>
        <Button
          primary
          loading={!!busy}
          disabled={!canSplit}
          onClick={() => onSplit({
            anchorLineId: row.lineId,
            kind: "split",
            parts: [
              /**
               * ⛔ `speakerRaw: null` ON THE HEAD, NOT A COPY OF THE ANCHOR'S SPEAKER.
               *
               * The projection reads `speaker: p.speakerRaw == null ? row.speaker : p.speakerRaw`, so
               * null means INHERIT and a copy means OVERRIDE — and the head always keeps the anchor's
               * speaker, which is inheritance, not a decision.
               *
               * ⚠️ Copying it broke the workflow this modal exists to serve (Ghost ch34,
               * 2026-10-03): the operator split a line with glued Narrator prose, then reassigned the
               * speech to Tsaark-ka — and the part went on reading as the Narrator, because it carried
               * its own stamped "Narrator" that the anchor's reassignment could not reach. The emotion
               * field one line down already gets this right; the speaker did not.
               */
              { type: row.type || "dialogue", speakerRaw: null, content: head },
              // The tail is a real decision — a Narrator beat cut out of a character's line names its
              // own speaker — so it stays explicit.
              { type: tailType, speakerRaw: tailSpeaker || null, content: tail },
            ],
          })}
        >
          <Icon name="cut" /> Split into two lines
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default LineSplitModal;
